<template>
    <div class="xeHistoryRange heightMAX rounded" style="overflow: hidden">
        <div class="heightMAX" style="padding: 0">
            <drop
                @dragover="handleBodyDragOver(...arguments)"
                @drop="handleBodyDrop(...arguments)"
                class="heightMAX kbet-custom-reset"
            >
                <div class="rounded kbet-widget-energy heightMAX" style="display: block">
                    <div class="title rounded-top d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                            <span>{{ $t(widget.title) }}</span>
                            <span class="period-display">{{ displayPeriodRange }}</span>
                        </div>
                        <div class="safari-style-buttons">
                            <label class="safari-button red">
                                <input type="radio" value="DAY" v-model="periodMode" />
                                <span class="button-dot"></span>
                            </label>
                            <label class="safari-button yellow">
                                <input type="radio" value="MONTH" v-model="periodMode" />
                                <span class="button-dot"></span>
                            </label>
                            <label class="safari-button green">
                                <input type="radio" value="YEAR" v-model="periodMode" />
                                <span class="button-dot"></span>
                            </label>
                        </div>
                    </div>
                    <div class="chartHeightMax">
                        <div class="chartHeightMax p-5">
                            <!-- 관제점이 설정되지 않은 경우 -->
                            <div v-if="!hasPoints" class="empty-state">
                                <div class="text-center">
                                    <i class="fas fa-info-circle empty-state-icon"></i>
                                    <p class="mb-0 empty-state-title">
                                        {{ $t("관제점을 선택해주세요") }}
                                    </p>
                                    <p class="mt-2 mb-0 empty-state-subtitle">
                                        {{ $t("설정에서 관제점을 추가하여 데이터를 조회할 수 있습니다") }}
                                    </p>
                                </div>
                            </div>
                            <!-- 차트 표시 -->
                            <div v-else v-show="viewMode === 'Chart'" ref="chart" class="chart-container"></div>
                        </div>
                    </div>
                </div>
            </drop>
        </div>
        <b-modal
            ref="modalSetting"
            size="lg"
            :title="`${$t('설정')} - 트렌드 ${$t('차트')}`"
            bodyClass="p-t-0 p-b-0"
            hide-footer
        >
            <chart-line-setting
                :initSetting="chartSetting"
                :data="eCharts.data"
                @on-modal-cancel="onModalCancel"
                @on-modal-ok="onModalOK"
            />
        </b-modal>
    </div>
</template>

<script>
import xeBaseWidget from "@src/views/widget/v1/common/xeBaseWidget.js";
import * as echarts from "echarts";
import chartLineSetting from "./setting/xeHistoryRangeSetting.vue";
import utils from "@src/views/widget/v1/common/xeBaseUtils.js";

// 상수 정의
const PERIOD_MODES = {
    DAY: "DAY",
    MONTH: "MONTH",
    YEAR: "YEAR",
};

const TIME_TYPES = {
    YEAR: 2,
    MONTH: 3,
    DAY: 4,
};

const DEFAULT_COLORS = {
    FONT: "#000000",
    GRID: "#0000001F",
    Y_AXIS: "#9B9B9BFF",
    BACKGROUND: "transparent",
};

const PERIOD_CONFIG = {
    [PERIOD_MODES.DAY]: {
        timeType: TIME_TYPES.DAY,
        sTi: "now.sDate(0)",
        eTi: "now.eDate(0)",
    },
    [PERIOD_MODES.MONTH]: {
        timeType: TIME_TYPES.MONTH,
        sTi: "now.addMonth(-1).sDate(0)",
        eTi: "now.eDate(0)",
    },
    [PERIOD_MODES.YEAR]: {
        timeType: TIME_TYPES.YEAR,
        sTi: "now.addYear(-1).sMonth(0)",
        eTi: "now.eMonth(0)",
    },
};

export default {
    name: "xeHistoryRange",
    extends: xeBaseWidget,
    components: {
        chartLineSetting,
    },
    props: {
        cardHeight: {
            type: Number,
            default: 1,
        },
        cardWidth: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            chart: null,
            eCharts: {
                data: {},
            },
            viewMode: "Chart",
            periodMode: this.widget.periodMode || PERIOD_MODES.MONTH,
            resizeHandler: null,
        };
    },
    computed: {
        cardSize() {
            return this.cardHeight * this.cardWidth;
        },
        chartSetting() {
            return JSON.parse(JSON.stringify(this.widget));
        },
        hasPoints() {
            return (
                this.widget.chart?.series?.length > 0 && this.widget.chart.series.some((series) => series.point?.trim())
            );
        },
        displayPeriodRange() {
            const now = new Date();
            const formatters = {
                [PERIOD_MODES.DAY]: () => now.format("yyyy.MM.dd"),
                [PERIOD_MODES.MONTH]: () => {
                    const startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                    return `${startDate.format("yyyy.MM.dd")} - ${now.format("yyyy.MM.dd")}`;
                },
                [PERIOD_MODES.YEAR]: () => {
                    const startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
                    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    return `${startDate.format("yyyy.MM")} - ${endDate.format("yyyy.MM")}`;
                },
            };

            return formatters[this.periodMode]?.() || "";
        },
    },
    watch: {
        periodMode(newVal) {
            this.widget.periodMode = newVal;
            this.updatePeriodRange();
            this.$nextTick(() => {
                this.$emit("on-period-change");
            });
        },
        cardSize() {
            this.chart?.resize();
        },
        eCharts: {
            handler() {
                this.renderChart();
            },
            deep: true,
        },
    },
    created() {
        this.initializeWidgetStructure();
        this.updatePeriodRange();
    },
    mounted() {
        this.setupParentHeight();
        this.$nextTick(() => {
            this.initChart();
        });
    },
    beforeDestroy() {
        this.cleanupChart();
    },
    methods: {
        // ========== 초기화 메서드 ==========
        initializeWidgetStructure() {
            this.initAddition();
            this.initPeriodMode();
            this.initChart();
            this.initChartOptions();
            this.initLegendOptions();
            this.initYAxesOptions();
            this.initSelfStructure();
        },

        initAddition() {
            if (!this.widget.addition) {
                this.widget.addition = {
                    fixed: 0,
                    afterAction: "",
                };
            }
        },

        initPeriodMode() {
            if (!this.widget.periodMode) {
                this.widget.periodMode = PERIOD_MODES.MONTH;
            }
            this.periodMode = this.widget.periodMode;
        },

        initChartStructure() {
            if (!this.widget.chart) {
                this.widget.chart = {};
            }

            this.widget.chart.backgroundColor = this.widget.chart.backgroundColor || DEFAULT_COLORS.BACKGROUND;
            this.widget.chart.chartFontSize = this.widget.chart.chartFontSize || 12;
            this.widget.chart.series = this.widget.chart.series || [];
        },

        initChartOptions() {
            if (!this.widget.chart) {
                this.widget.chart = {};
            }
            if (!this.widget.chart.options) {
                this.widget.chart.options = {};
            }
        },

        initLegendOptions() {
            const defaultLegend = {
                display: true,
                labels: {
                    fontSize: 12,
                    fontColor: DEFAULT_COLORS.FONT,
                },
            };

            if (!this.widget.chart) {
                this.widget.chart = {};
            }
            if (!this.widget.chart.options) {
                this.widget.chart.options = {};
            }
            if (!this.widget.chart.options.legend) {
                this.widget.chart.options.legend = defaultLegend;
            }
            if (!this.widget.chart.options.legend.labels) {
                this.widget.chart.options.legend.labels = defaultLegend.labels;
            }
        },

        initYAxesOptions() {
            const defaultYAxes = {
                left: {
                    display: true,
                    label: "",
                    unit: "",
                    fontSize: 12,
                    fontColor: DEFAULT_COLORS.Y_AXIS,
                },
                right: {
                    display: false,
                    label: "",
                    unit: "",
                    fontSize: 12,
                },
            };

            if (!this.widget.chart.options.yAxes) {
                this.widget.chart.options.yAxes = defaultYAxes;
            }
            if (!this.widget.chart.options.yAxes.left) {
                this.widget.chart.options.yAxes.left = defaultYAxes.left;
            }
            if (!this.widget.chart.options.yAxes.left.fontColor) {
                this.widget.chart.options.yAxes.left.fontColor = DEFAULT_COLORS.Y_AXIS;
            }
            if (!this.widget.chart.options.yAxes.right) {
                this.widget.chart.options.yAxes.right = defaultYAxes.right;
            }
        },

        initSelfStructure() {
            if (!this.widget.self) {
                this.widget.self = {
                    baseline: {
                        type: TIME_TYPES.DAY,
                        sTi: "",
                        eTi: "",
                    },
                    compare: {
                        type: TIME_TYPES.DAY,
                        sTi: "",
                        eTi: "",
                    },
                };
            }
        },

        setupParentHeight() {
            const ranges = document.getElementsByClassName("xeHistoryRange");
            Array.from(ranges).forEach((range) => {
                range.parentNode.style.height = "100%";
            });
        },

        // ========== 기간 관련 메서드 ==========
        updatePeriodRange() {
            const config = PERIOD_CONFIG[this.periodMode] || PERIOD_CONFIG[PERIOD_MODES.MONTH];

            if (!this.widget.self) {
                this.widget.self = {
                    baseline: { type: config.timeType, sTi: "", eTi: "" },
                    compare: { type: config.timeType, sTi: "", eTi: "" },
                };
            }

            this.widget.self.baseline = {
                type: config.timeType,
                sTi: config.sTi,
                eTi: config.eTi,
            };

            this.$emit("on-change-widget", this.widget);
        },

        // ========== 차트 관련 메서드 ==========
        initChart() {
            if (this.chart) {
                this.chart.dispose();
            }
            this.$nextTick(() => {
                if (!this.$refs.chart) return;

                this.chart = echarts.init(this.$refs.chart);
                this.renderChart();
                this.setupChartResize();
            });
        },

        setupChartResize() {
            setTimeout(() => {
                this.chart?.resize();
            }, 100);
        },

        renderChart() {
            if (!this.eCharts.data?.datasets || !this.chart) {
                return;
            }

            const option = this.buildChartOption();
            this.chart.setOption(option);
            this.attachResizeListener();
        },

        buildChartOption() {
            const series = this.buildSeriesData();

            return {
                grid: this.buildGridConfig(),
                tooltip: this.buildTooltipConfig(),
                legend: this.buildLegendConfig(series),
                xAxis: this.buildXAxisConfig(),
                yAxis: this.buildYAxisConfig(),
                series,
            };
        },

        buildGridConfig() {
            return {
                left: "3%",
                right: "3%",
                bottom: "12%",
                top: "20%",
                containLabel: true,
            };
        },

        buildTooltipConfig() {
            return {
                confine: false,
                appendToBody: true,
                trigger: "axis",
                backgroundColor: "rgba(0,0,0,0.8)",
                textStyle: {
                    color: "#fff",
                    fontSize: 12,
                },
                borderWidth: 0,
                axisPointer: {
                    type: "cross",
                    label: {
                        backgroundColor: "#6a7985",
                    },
                },
                formatter: this.formatTooltip,
            };
        },

        formatTooltip(params) {
            let result = `${params[0].axisValue}<br/>`;
            params.forEach((item) => {
                const value = Number(item.data);
                const formattedValue = this.widget.addition.fixed
                    ? value.toFixed(Number(this.widget.addition.fixed))
                    : value;
                result += `${item.marker} ${item.seriesName} : ${formattedValue}<br/>`;
            });
            return result;
        },

        buildLegendConfig(series) {
            return {
                data: series.map((s) => s.name),
                show: this.widget.chart.options.legend.display,
                orient: this.getLegendOrient(),
                left: this.getLegendLeft(),
                top: this.getLegendTop(),
                icon: "circle",
                textStyle: {
                    color: this.widget.chart.options.legend.labels.fontColor || DEFAULT_COLORS.FONT,
                    fontSize: Number(this.widget.chart.options.legend.labels.fontSize) || 14,
                },
            };
        },

        buildXAxisConfig() {
            return {
                type: "category",
                boundaryGap: true,
                data: this.eCharts.data.labels || [],
                axisLabel: {
                    color: this.widget.chart.fontColor || DEFAULT_COLORS.FONT,
                    fontSize: this.widget.chart.chartFontSize || 9,
                },
                axisLine: {
                    lineStyle: {
                        color: this.widget.chart.gridColor || DEFAULT_COLORS.GRID,
                    },
                },
            };
        },

        buildYAxisConfig() {
            const leftAxis = this.widget.chart.options.yAxes.left;

            return [
                {
                    type: "value",
                    position: "left",
                    name: leftAxis.label,
                    nameLocation: "end",
                    nameGap: 10,
                    nameRotate: 0,
                    axisLabel: {
                        color: leftAxis.fontColor || DEFAULT_COLORS.FONT,
                        fontSize: leftAxis.fontSize || 14,
                        show: leftAxis.display,
                        formatter: this.formatAxisValue,
                    },
                    axisLine: {
                        show: false,
                    },
                    splitLine: {
                        show: leftAxis.display,
                        lineStyle: {
                            color: this.widget.chart.gridColor || DEFAULT_COLORS.GRID,
                        },
                    },
                    nameTextStyle: {
                        color: leftAxis.fontColor || DEFAULT_COLORS.FONT,
                        fontSize: leftAxis.fontSize || 14,
                        align: "left",
                    },
                },
                this.buildRightYAxis(),
            ];
        },

        buildRightYAxis() {
            const rightAxis = this.widget.chart.options.yAxes.right;

            return {
                type: "value",
                position: "right",
                name: rightAxis.label,
                nameLocation: "middle",
                nameGap: 40,
                axisLabel: {
                    show: rightAxis.display,
                    color: rightAxis.fontColor || DEFAULT_COLORS.FONT,
                    fontSize: rightAxis.fontSize || 14,
                },
                axisLine: {
                    show: rightAxis.display,
                    lineStyle: {
                        color: this.widget.chart.gridColor || DEFAULT_COLORS.GRID,
                    },
                },
                splitLine: {
                    show: rightAxis.display,
                    lineStyle: {
                        color: this.widget.chart.gridColor || DEFAULT_COLORS.GRID,
                    },
                },
                nameTextStyle: {
                    color: rightAxis.fontColor || DEFAULT_COLORS.FONT,
                    fontSize: rightAxis.fontSize || 14,
                },
            };
        },

        formatAxisValue(value) {
            const scales = [
                { threshold: 1e9, suffix: "G" },
                { threshold: 1e6, suffix: "M" },
                { threshold: 1e3, suffix: "K" },
            ];

            for (const scale of scales) {
                if (value >= scale.threshold) {
                    const scaledValue = value / scale.threshold;
                    return Number.isInteger(scaledValue)
                        ? `${scaledValue}${scale.suffix}`
                        : `${scaledValue.toFixed(1)}${scale.suffix}`;
                }
            }

            return Number.isInteger(value) ? value.toString() : value.toFixed(1);
        },

        buildSeriesData() {
            const isStacked = this.widget.chart.options?.stacked;

            return this.eCharts.data.datasets.map((dataset) => ({
                name: dataset.label || "Default",
                type: "line",
                smooth: true,
                symbol: "circle",
                symbolSize: 4,
                showSymbol: true,
                data: dataset.data,
                stack: isStacked ? "total" : undefined,
                itemStyle: {
                    color: dataset.backgroundColor || dataset.borderColor,
                },
                lineStyle: {
                    width: dataset.borderWidth || 1.5,
                },
                areaStyle: {
                    color: dataset.backgroundColor || dataset.borderColor,
                    opacity: 0.3,
                },
                yAxisIndex: dataset.yAxisID === "Left_yAxis" ? 0 : 1,
            }));
        },

        attachResizeListener() {
            if (this.resizeHandler) {
                window.removeEventListener("resize", this.resizeHandler);
            }
            this.resizeHandler = () => {
                this.chart?.resize();
            };
            window.addEventListener("resize", this.resizeHandler);
        },

        cleanupChart() {
            if (this.resizeHandler) {
                window.removeEventListener("resize", this.resizeHandler);
                this.resizeHandler = null;
            }
            if (this.chart) {
                this.chart.dispose();
                this.chart = null;
            }
        },

        // ========== 데이터 처리 메서드 ==========
        initSetting() {
            this.eCharts.data = {
                labels: this.defaultLabels(this.widget.self?.baseline?.type),
                datasets: this.defaultDatasets(),
            };
            this.renderChart();
        },

        defaultLabels() {
            return [];
        },

        createLabels(logs) {
            if (!logs || logs.length === 0) return [];

            const formattedLogs = this.formatLogDates(logs);
            return this.extractLabelsFromLogs(formattedLogs);
        },

        formatLogDates(logs) {
            const dateFormatters = {
                4: (str) => `${str.substr(0, 4)}${global.xe.$t("년")}`, // YYYY
                6: (str) => global.xe.$t(`${str.substr(4, 2)}월`), // YYYYMM
                8: (str) => global.xe.$t(`${Number(str.substr(6, 2))}일`), // YYYYMMDD
                10: (str) => `${str.substr(8, 2)}${global.xe.$t("시")}`, // YYYYMMDDHH
                12: (str) => `${str.substr(8, 2)}:${str.substr(10, 2)}`, // YYYYMMDDHHMM
            };

            return logs.map((log) => {
                const formattedRows = log.rows.map((str) => {
                    const length = str.length;
                    return dateFormatters[length]?.(str) || str;
                });
                return { ...log, rows: formattedRows };
            });
        },

        extractLabelsFromLogs(logs) {
            if (logs.length === 1) {
                return logs[0].rows;
            }
            if (logs.length === 2) {
                const [base, comp] = logs;
                const longerLog = base.rows.length >= comp.rows.length ? base : comp;
                return longerLog.rows.map((dt) => [dt]);
            }
            return [];
        },

        defaultDatasets() {
            return this.widget.chart?.series
                ?.filter((item) => !item.compare || this.isCompare)
                .map((item) => ({
                    backgroundColor: item.dataColor,
                    borderColor: item.dataColor,
                    fill: true,
                    label: global.xe.$t(item.label),
                    borderWidth: 1.5,
                    pointRadius: 0,
                    showLine: true,
                    borderCapStyle: "butt",
                    spanGaps: true,
                    yAxisID: item.position === "Left" ? "Left_yAxis" : "Right_yAxis",
                    point: item.point,
                    unit: item.unit,
                    compare: item.compare || false,
                }));
        },

        createDatasets(logs) {
            const datasets = this.defaultDatasets();
            const processedLogs = utils.onAfterAction2(
                datasets,
                JSON.parse(JSON.stringify(logs)),
                this.widget.addition.afterAction
            );

            return datasets.map((dataset) => {
                const log = dataset.compare ? processedLogs[1] : processedLogs[0];
                const ptVals = log?.points?.[dataset.point] || [];
                return { ...dataset, data: ptVals };
            });
        },

        applyData(widgetLogs) {
            this.eCharts.data = {
                labels: this.createLabels(widgetLogs),
                datasets: this.createDatasets(widgetLogs),
            };
            this.renderChart();
            this.widgetLogs = widgetLogs;
        },

        // ========== 드래그 앤 드롭 메서드 ==========
        handleDropPoint(transfer) {
            const points = transfer.item.type === this.CODE.Drag.PointGroup ? transfer.item.points : [transfer.item];

            this.removeDefaultSeriesIfExists();
            this.addPointsToSeries(points);
        },

        removeDefaultSeriesIfExists() {
            const defaultObj = {
                type: "spline",
                label: "Default",
                point: "",
                position: "Left",
                backgroundColor: "rgba(52, 143, 226, 0.3)",
                fill: false,
                compare: false,
            };

            if (JSON.stringify(this.widget.chart?.series?.slice(0, 1)) === JSON.stringify([defaultObj])) {
                this.widget.chart.series = [];
            }
        },

        addPointsToSeries(points) {
            points.forEach((point) => {
                const exists = this.widget.chart?.series?.some((v) => v.point === point.ptAddr);
                if (!exists) {
                    this.widget.chart.series.push({
                        type: "spline",
                        label: point.title,
                        point: point.ptAddr,
                        position: "Left",
                        unit: "",
                        backgroundColor: this.getRandomColor(),
                        fill: false,
                    });
                }
            });
        },

        // ========== Legend 위치 메서드 ==========
        getLegendOrient() {
            const position = this.widget.chart.options.legend.position || "top";
            return ["left", "right"].includes(position) ? "vertical" : "horizontal";
        },

        getLegendLeft() {
            const position = this.widget.chart.options.legend.position || "top";
            const align = this.widget.chart.options.legend.align || "center";

            const positionMap = {
                left: "left",
                right: "right",
                top: this.getAlignValue(align),
                bottom: this.getAlignValue(align),
            };

            return positionMap[position] || "center";
        },

        getLegendTop() {
            const position = this.widget.chart.options.legend.position || "top";
            const align = this.widget.chart.options.legend.align || "center";

            const positionMap = {
                top: "top",
                bottom: "bottom",
                left: this.getAlignValue(align),
                right: this.getAlignValue(align),
            };

            return positionMap[position] || "top";
        },

        getAlignValue(align) {
            const alignMap = {
                start: "left",
                center: "center",
                end: "right",
            };
            return alignMap[align] || "center";
        },
    },
};
</script>

<style lang="scss" scoped>
.period-display {
    margin-left: 12px;
    font-size: 11px;
    font-weight: 400;
    color: #999;
    opacity: 0.8;
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}

.empty-state-icon {
    font-size: 48px;
    color: #999;
    margin-bottom: 16px;
}

.empty-state-title {
    font-size: 16px;
    color: #666;
}

.empty-state-subtitle {
    font-size: 14px;
    color: #999;
}

.chart-container {
    width: 100%;
    height: 100%;
}

.safari-style-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
}

.safari-button {
    position: relative;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }

    input[type="radio"] {
        position: absolute;
        opacity: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;

        &:checked {
            box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);

            + .button-dot {
                opacity: 1;
            }
        }
    }

    &.red {
        background: linear-gradient(135deg, #ff5f57 0%, #ff4444 100%);
    }

    &.yellow {
        background: linear-gradient(135deg, #ffbd2e 0%, #ffb300 100%);
    }

    &.green {
        background: linear-gradient(135deg, #28ca42 0%, #4caf50 100%);
    }
}

.button-dot {
    width: 4px;
    height: 4px;
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.2s ease;
}
</style>
